import React from "react";
import { Card, Row, Col, Badge, Table } from "react-bootstrap";
import { useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import {
  FaTruck,
  FaRoute,
  FaDollarSign,
  FaMapMarkerAlt,
  FaClock,
} from "react-icons/fa";
import moment from "moment";

function FleetDashboard() {
  const { backendUrl } = useAuth();

  // Fetch dashboard data
  const fetchDashboard = async () => {
    const response = await fetch(`${backendUrl}/api/fleet/dashboard`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const result = await response.json();
    return result.data;
  };

  const { data, isLoading, error } = useQuery(
    ["FLEET_DASHBOARD"],
    () => fetchDashboard(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        Error loading dashboard: {error.message}
      </div>
    );
  }

  const vehicleStatus = data?.vehicleStatus || [];
  const activeTrips = data?.activeTrips || 0;
  const expensesTodayTotal = data?.expensesTodayTotal || 0;
  const latestLocations = data?.latestLocations || [];

  return (
    <section className="invoice-wrapped">
      <main className="cash-book">
        <div className="content">
          <div className="content-main">
            <header className="text-nowrap">
              <h1>Fleet Dashboard</h1>
            </header>

            <div className="px-md-4">
              {/* Key Metrics */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <FaTruck size={32} className="text-primary mb-2" />
                      <h3 className="text-primary">
                        {vehicleStatus.reduce((sum, status) => sum + status.count, 0)}
                      </h3>
                      <small className="text-muted">Total Vehicles</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <FaRoute size={32} className="text-success mb-2" />
                      <h3 className="text-success">{activeTrips}</h3>
                      <small className="text-muted">Active Trips</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <FaDollarSign size={32} className="text-warning mb-2" />
                      <h3 className="text-warning">
                        {formatCurrency(expensesTodayTotal)}
                      </h3>
                      <small className="text-muted">Today's Expenses</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center">
                    <Card.Body>
                      <FaMapMarkerAlt size={32} className="text-info mb-2" />
                      <h3 className="text-info">{latestLocations.length}</h3>
                      <small className="text-muted">Vehicles Tracked</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Vehicle Status Breakdown */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <FaTruck className="me-2" />
                      Vehicle Status
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex flex-wrap gap-2">
                        {vehicleStatus.map((status) => (
                          <Badge
                            key={status.status}
                            bg={
                              status.status === "available"
                                ? "success"
                                : status.status === "in_use"
                                ? "primary"
                                : status.status === "maintenance"
                                ? "warning"
                                : status.status === "out_of_service"
                                ? "danger"
                                : "secondary"
                            }
                            className="p-2"
                          >
                            {status.status.replace("_", " ").toUpperCase()}: {status.count}
                          </Badge>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <FaMapMarkerAlt className="me-2" />
                      Latest Vehicle Locations
                    </Card.Header>
                    <Card.Body>
                      {latestLocations.length > 0 ? (
                        <Table responsive borderless size="sm">
                          <thead>
                            <tr>
                              <th>Vehicle</th>
                              <th>Location</th>
                              <th>Speed</th>
                              <th>Status</th>
                              <th>Last Update</th>
                            </tr>
                          </thead>
                          <tbody>
                            {latestLocations.slice(0, 5).map((location, index) => (
                              <tr key={index}>
                                <td className="fw-semibold">
                                  {location.vehicle_id}
                                </td>
                                <td>
                                  <small>
                                    {location.latitude?.toFixed(4)}, {location.longitude?.toFixed(4)}
                                  </small>
                                </td>
                                <td>{location.speed ? `${location.speed} km/h` : "N/A"}</td>
                                <td>
                                  <Badge
                                    bg={
                                      location.engineStatus === "running"
                                        ? "success"
                                        : "secondary"
                                    }
                                  >
                                    {location.engineStatus || "unknown"}
                                  </Badge>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {moment(location.timestamp).fromNow()}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="text-center text-muted py-3">
                          <FaMapMarkerAlt size={24} className="mb-2 opacity-50" />
                          <p>No location data available</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Recent Activity Placeholder */}
              <Card>
                <Card.Header>
                  <FaClock className="me-2" />
                  Recent Activity
                </Card.Header>
                <Card.Body>
                  <div className="text-center text-muted py-4">
                    <FaClock size={48} className="mb-3 opacity-50" />
                    <p>Activity feed coming soon...</p>
                    <small>This will show recent trips, maintenance, and vehicle updates</small>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </section>
  );
}

export default FleetDashboard;
